"use client";

import { Fragment, useMemo, type ReactNode } from "react";
import Link from "next/link";
import { useParams, usePathname } from "next/navigation";
import { MoreHorizontal } from "lucide-react";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { sidebarItems as defaultSidebarItems } from "@/components/sidebar/data";
import type { SideBarItem, SidebarItemsLink } from "@/components/sidebar/types";
type BreadcrumbCrumb = {
  title: string;
  link?: string;
  newTab?: boolean;
  menuItems?: BreadcrumbMenuItem[];
};
type BreadcrumbMenuItem = {
  title: string;
  link: string;
  newTab?: boolean;
  basePath?: string;
};
type NavBarProps = {
  items?: SideBarItem[];
  className?: string;
  includeHome?: boolean;
  mainInfoLayout?: boolean;
  subItemName?: string;
  entityName?: string; // Pass entity name for dynamic breadcrumb titles
  isParent?: boolean; //  Pass if this is a parent breadcrumb
  rightSlot?: ReactNode;
};
const isExternalLink = (link?: string) => !!link && /^https?:\/\//i.test(link);
const normalizePath = (value: string) => {
  if (!value) return "/";
  const withSlash = value.startsWith("/") ? value : `/${value}`;
  const trimmed = withSlash.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
};
const UUID_SEGMENT_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MANAGEMENT_ROUTE_DYNAMIC_MODULES: Record<string, string> = {
  "/academies/academies-management": "academies",
  "/performance-plus/sports-management": "performance-plus",
  "/assets-rental/assets-management": "assets-rental",
  "/dining/restaurants": "dining"
};
const matchesDynamicEntityPath = (path: string, moduleName: string) => {
  const segments = normalizePath(path).split("/").filter(Boolean);
  if (segments[0] !== moduleName) return false;
  if (!segments[1]) return false;
  return UUID_SEGMENT_REGEX.test(segments[1]);
};
const isManagementItemActiveForDynamicRoute = (menuLink: string, currentPath: string) => {
  const moduleName = MANAGEMENT_ROUTE_DYNAMIC_MODULES[menuLink];
  if (!moduleName) return false;
  return matchesDynamicEntityPath(currentPath, moduleName);
};
const stripBranchPrefix = (value: string, branchId?: string) => {
  if (!branchId) return value;
  const prefix = `/${branchId}`;
  if (!value.startsWith(prefix)) return value;
  const trimmed = value.slice(prefix.length);
  return trimmed === "" ? "/" : trimmed;
};
const toCrumb = (item: {
  title: string;
  link?: string;
  newTab?: boolean;
}): BreadcrumbCrumb => ({
  title: item.title,
  link: item.link,
  newTab: item.newTab
});
const toMenuItems = (items?: SidebarItemsLink[], basePath?: string) => {
  if (!items) return [];
  const menuItems: BreadcrumbMenuItem[] = [];
  for (const item of items) {
    if (item.link) {
      menuItems.push({
        title: item.title,
        link: item.link,
        newTab: item.newTab,
        basePath
      });
    }
    if (item.subLinks?.length) {
      for (const subItem of item.subLinks) {
        if (!subItem.link) continue;
        menuItems.push({
          title: subItem.title,
          link: subItem.link,
          newTab: subItem.newTab,
          basePath
        });
      }
    }
  }
  return menuItems;
};
const getParentLink = (item: SideBarItem) => item.link ?? item.sidebarItemsLinks?.find(child => child.link)?.link ?? item.itemLinkSidebarItems?.find(child => child.link)?.link;
const toParentCrumb = (item: SideBarItem): BreadcrumbCrumb => ({
  title: item.title,
  link: getParentLink(item),
  newTab: item.newTab,
  menuItems: toMenuItems(item.sidebarItemsLinks)
});
const findBreadcrumbs = (items: SideBarItem[], path: string, entityName?: string) => {
  const normalizedPath = normalizePath(path);
  const pathSegments = normalizedPath.split("/").filter(Boolean);
  const rootSegment = pathSegments[0] ?? "";
  const matchesLink = (link?: string) => {
    if (!link || isExternalLink(link)) return false;
    const normalizedLink = normalizePath(link);
    if (normalizedLink === "/") return normalizedPath === "/";
    return normalizedPath === normalizedLink || normalizedPath.startsWith(`${normalizedLink}/`);
  };
  const matchesSuffix = (link?: string) => {
    if (!link || isExternalLink(link)) return false;
    const normalizedLink = normalizePath(link);
    if (normalizedLink === "/") return false;
    return normalizedPath.endsWith(normalizedLink);
  };
  for (const item of items) {
    if (item.title === "divider") continue;
    if (matchesLink(item.link)) {
      return [toParentCrumb(item)];
    }
    const canConsiderChildren = !item.url || rootSegment === "" || item.url === rootSegment;
    if (canConsiderChildren && item.sidebarItemsLinks) {
      for (const subItem of item.sidebarItemsLinks) {
        if (matchesLink(subItem.link)) {
          return [toParentCrumb(item), toCrumb(subItem)];
        }
        if (subItem.subLinks) {
          for (const subLink of subItem.subLinks) {
            if (matchesLink(subLink.link)) {
              return [toParentCrumb(item), toCrumb(subItem), toCrumb(subLink)];
            }
          }
        }
      }
    }
    if (canConsiderChildren && item.itemLinkSidebarItems) {
      for (const subItem of item.itemLinkSidebarItems) {
        if (matchesSuffix(subItem.link)) {
          const suffixSegments = normalizePath(subItem.link ?? "").split("/").filter(Boolean);
          const suffixStartIndex = pathSegments.length - suffixSegments.length;
          const slugSegmentIndex = suffixStartIndex - 1;
          const slugSegment = pathSegments[slugSegmentIndex];
          const slugCrumb: BreadcrumbCrumb | null = slugSegment ? (() => {
            try {
              return {
                title: entityName || "",
                // link: `/${rootSegment}/${slugSegment}`,
                menuItems: toMenuItems(item.itemLinkSidebarItems, `/${rootSegment}/${slugSegment}`)
              };
            } catch {
              return null;
            }
          })() : null;
          return slugCrumb ? [toParentCrumb(item), slugCrumb, toCrumb(subItem)] : [toParentCrumb(item), toCrumb(subItem)];
        }
      }
    }
  }
  return [];
};
const buildHref = (link: string | undefined, branchId?: string) => {
  if (!link || isExternalLink(link)) return link;
  if (!branchId) return link;
  if (link === "/") return `/${branchId}`;
  return `/${branchId}${link}`;
};
export const BreadcrumbNav = ({
  items = defaultSidebarItems,
  className,
  includeHome = true,
  mainInfoLayout = false,
  entityName,
  isParent = false,
  rightSlot
}: NavBarProps) => {
  const pathname = usePathname();
  const params = useParams();
  const branchId = params.branch_id as string | undefined;
  const pathWithoutBranch = stripBranchPrefix(pathname, branchId);
  const normalizedPath = normalizePath(pathWithoutBranch);
  const breadcrumbs = useMemo(() => {
    const found = findBreadcrumbs(items, pathWithoutBranch, entityName);
    if (!includeHome) return found;
    const homeItem = items.find(item => item.link === "/");
    if (!homeItem) return found;
    if (found.length === 0) return [toCrumb(homeItem)];
    if (found[0].title === homeItem.title) return found;
    return [toCrumb(homeItem), ...found];
  }, [includeHome, items, pathWithoutBranch, entityName]);
  if (breadcrumbs.length === 0) {
    return null;
  }
  if (!mainInfoLayout && !entityName && !isParent) {
    return null;
  }
  const isOnlyHome = breadcrumbs.length === 1 && normalizePath(breadcrumbs[0].link ?? "") === "/";
  if (isOnlyHome) {
    return null;
  }
  type DisplayItem = {
    type: "crumb";
    crumb: BreadcrumbCrumb;
  } | {
    type: "menu";
    menuItems: BreadcrumbMenuItem[];
  };
  const displayItems: DisplayItem[] = breadcrumbs.flatMap((crumb, index) => {
    const items_0: DisplayItem[] = [{
      type: "crumb",
      crumb
    }];
    if ((crumb.menuItems?.length ?? 0) > 0 && index < breadcrumbs.length - 1) {
      items_0.push({
        type: "menu",
        menuItems: crumb.menuItems ?? []
      });
    }
    return items_0;
  });
  const breadcrumbNode = <Breadcrumb className={className}>
      <BreadcrumbList>
        {displayItems.map((item_0, index_0) => {
        const isLast = index_0 === displayItems.length - 1;
        if (item_0.type === "menu" && item_0.menuItems.length >= 1) {
          return <Fragment key={`menu-${index_0}`}>
                <BreadcrumbItem>
                  <DropdownMenu>
                    <DropdownMenuTrigger className="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent text-muted-foreground outline-none transition-colors hover:border-primary/25 hover:bg-primary/10 hover:text-primary focus-visible:border-primary/30 focus-visible:bg-primary/10 focus-visible:text-primary focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background data-[state=open]:border-primary/25 data-[state=open]:bg-primary/10 data-[state=open]:text-primary" aria-label="Open related pages menu">
                      <MoreHorizontal className="h-3.5 w-3.5" />
                      <span className="sr-only">Open menu</span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[10rem]">
                      {item_0.menuItems.map(menuItem => {
                    const menuLink = menuItem.basePath && !isExternalLink(menuItem.link) ? normalizePath(`${menuItem.basePath}${menuItem.link}`) : menuItem.link;
                    const menuHref = buildHref(menuLink, branchId);
                    if (!menuHref) return null;
                    const menuLinkProps = menuItem.newTab ? {
                      target: "_blank",
                      rel: "noreferrer"
                    } : undefined;
                    const normalizedMenuLink = normalizePath(menuLink);
                    const isActiveMenuItem = !isExternalLink(menuHref) && (normalizedMenuLink === normalizedPath || normalizedPath.endsWith(normalizedMenuLink) || isManagementItemActiveForDynamicRoute(normalizedMenuLink, normalizedPath));
                    const menuItemClassName = isActiveMenuItem ? "cursor-pointer bg-primary font-medium text-primary-foreground transition-colors hover:bg-primary/80 hover:text-primary-foreground focus:bg-primary/80 focus:text-primary-foreground data-[highlighted]:bg-primary/80 data-[highlighted]:text-primary-foreground" : "cursor-pointer text-foreground transition-colors hover:bg-primary/15 hover:text-primary focus:bg-primary/15 focus:text-primary data-[highlighted]:bg-primary/15 data-[highlighted]:text-primary";
                    return isExternalLink(menuHref) ? <DropdownMenuItem key={menuItem.link} className={menuItemClassName} asChild>
                            <a href={menuHref} {...menuLinkProps}>
                              {menuItem.title}
                            </a>
                          </DropdownMenuItem> : <DropdownMenuItem key={menuItem.link} className={menuItemClassName} asChild>
                            <Link href={menuHref} {...menuLinkProps}>
                              {menuItem.title}
                            </Link>
                          </DropdownMenuItem>;
                  })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator />}
              </Fragment>;
        } else if (item_0.type === "menu") {
          return null;
        }
        const href = buildHref(item_0.crumb.link, branchId);
        const linkProps = item_0.crumb.newTab ? {
          target: "_blank",
          rel: "noreferrer"
        } : undefined;
        return <Fragment key={`${item_0.crumb.title}-${index_0}`}>
              <BreadcrumbItem>
                {isLast || !href ? <BreadcrumbPage>{item_0.crumb.title}</BreadcrumbPage> : isExternalLink(href) ? <BreadcrumbLink asChild>
                    <a href={href} {...linkProps}>
                      {item_0.crumb.title}
                    </a>
                  </BreadcrumbLink> : <BreadcrumbLink asChild>
                    <Link href={href} {...linkProps}>
                      {item_0.crumb.title}
                    </Link>
                  </BreadcrumbLink>}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator />}
            </Fragment>;
      })}
      </BreadcrumbList>
    </Breadcrumb>;
  if (!rightSlot) {
    return breadcrumbNode;
  }
  return <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">{breadcrumbNode}</div>
      <div className="shrink-0">{rightSlot}</div>
    </div>;
};